import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  AccountBalance as BankIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Assessment as ReportIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { BillingDashboard } from '../types/Billing';
import BillingOverview from './Billing/BillingOverview';
import ChargeCapture from './Billing/ChargeCapture';
import FeeSchedule from './Billing/FeeSchedule';
import ClaimGeneration from './Billing/ClaimGeneration';
import RemittancePosting from './Billing/RemittancePosting';
import AccountsReceivable from './Billing/AccountsReceivable';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`billing-tabpanel-${index}`}
      aria-labelledby={`billing-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `billing-tab-${index}`,
    'aria-controls': `billing-tabpanel-${index}`,
  };
}

const Billing: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [dashboardData, setDashboardData] = useState<BillingDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Simulate loading dashboard data
    // In a real application, this would be an API call
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Mock data for demonstration
        const mockData: BillingDashboard = {
          dailyCharges: 45670.50,
          dailyPayments: 38920.75,
          dailyAdjustments: 2840.25,
          totalAR: 1245600.00,
          daysInAR: 42.5,
          cleanClaimRate: 0.94,
          denialRate: 0.06,
          collectionRate: 0.96,
          agingBuckets: {
            current: 450000,
            thirtyDays: 320000,
            sixtyDays: 280000,
            ninetyDays: 120000,
            over120: 75600
          },
          topPayersByVolume: [
            { payerName: 'Medicare', claimCount: 156, totalAmount: 245000 },
            { payerName: 'Blue Cross Blue Shield', claimCount: 98, totalAmount: 198000 },
            { payerName: 'Aetna', claimCount: 67, totalAmount: 134000 },
            { payerName: 'United Healthcare', claimCount: 54, totalAmount: 108000 },
            { payerName: 'Medicaid', claimCount: 89, totalAmount: 89000 }
          ],
          recentActivity: [
            { type: 'payment', description: 'Medicare payment received', amount: 12500, timestamp: '2024-01-15T10:30:00Z' },
            { type: 'claim_submission', description: 'Batch 2024-001 submitted', amount: 45000, timestamp: '2024-01-15T09:15:00Z' },
            { type: 'denial', description: 'BCBS claim denied - auth required', amount: 1200, timestamp: '2024-01-15T08:45:00Z' },
            { type: 'charge', description: 'New lab orders processed', amount: 8900, timestamp: '2024-01-15T08:00:00Z' }
          ]
        };
        
        setDashboardData(mockData);
      } catch (err) {
        setError('Failed to load billing dashboard data');
        console.error('Error loading dashboard:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
        Billing Module
      </Typography>

      {/* Key Performance Indicators */}
      {dashboardData && currentTab === 0 && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <MoneyIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" component="h2">
                    Daily Charges
                  </Typography>
                </Box>
                <Typography variant="h4" color="primary">
                  {formatCurrency(dashboardData.dailyCharges)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Today's total charges
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PaymentIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6" component="h2">
                    Daily Payments
                  </Typography>
                </Box>
                <Typography variant="h4" color="success.main">
                  {formatCurrency(dashboardData.dailyPayments)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Today's total payments
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <BankIcon color="info" sx={{ mr: 1 }} />
                  <Typography variant="h6" component="h2">
                    Total A/R
                  </Typography>
                </Box>
                <Typography variant="h4" color="info.main">
                  {formatCurrency(dashboardData.totalAR)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {dashboardData.daysInAR.toFixed(1)} days in A/R
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUpIcon color="warning" sx={{ mr: 1 }} />
                  <Typography variant="h6" component="h2">
                    Collection Rate
                  </Typography>
                </Box>
                <Typography variant="h4" color="warning.main">
                  {formatPercentage(dashboardData.collectionRate)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Clean claim rate: {formatPercentage(dashboardData.cleanClaimRate)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Navigation Tabs */}
      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="billing module tabs"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': {
              minWidth: 120,
              fontSize: '0.9rem'
            }
          }}
        >
          <Tab 
            label="Overview" 
            icon={<ReportIcon />} 
            iconPosition="start"
            {...a11yProps(0)} 
          />
          <Tab 
            label="Charge Capture" 
            icon={<ReceiptIcon />} 
            iconPosition="start"
            {...a11yProps(1)} 
          />
          <Tab 
            label="Fee Schedule" 
            icon={<ScheduleIcon />} 
            iconPosition="start"
            {...a11yProps(2)} 
          />
          <Tab 
            label="Claim Generation" 
            icon={<MoneyIcon />} 
            iconPosition="start"
            {...a11yProps(3)} 
          />
          <Tab 
            label="Remittance Posting" 
            icon={<PaymentIcon />} 
            iconPosition="start"
            {...a11yProps(4)} 
          />
          <Tab 
            label="Accounts Receivable" 
            icon={<BankIcon />} 
            iconPosition="start"
            {...a11yProps(5)} 
          />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={currentTab} index={0}>
        <BillingOverview dashboardData={dashboardData} />
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        <ChargeCapture />
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        <FeeSchedule />
      </TabPanel>

      <TabPanel value={currentTab} index={3}>
        <ClaimGeneration />
      </TabPanel>

      <TabPanel value={currentTab} index={4}>
        <RemittancePosting />
      </TabPanel>

      <TabPanel value={currentTab} index={5}>
        <AccountsReceivable />
      </TabPanel>
    </Container>
  );
};

export default Billing;